import { useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { FormFieldProps, OptionProps } from '..';
import stylesPP from './scss/FormCheckboxesGroupPP.module.scss';
import stylesSSO from './scss/FormCheckboxesGroupSSO.module.scss';
import { defaultValidationMessage } from '../config/validators/validators';
import {
  hasValue,
  getProjectType,
  ProjectTypes,
  scrollToCenter,
  isEmpty,
} from '@r-and-a-shared-ui/utils';
import { RichTextParser } from '@r-and-a-shared-ui/common-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-regular-svg-icons';
// @ts-ignore
import { byPrefixAndName } from '@awesome.me/kit-93f54c413a/icons';

export function FormCheckboxesGroup(props: FormFieldProps) {
  const {
    name,
    label,
    isDisabled,
    validationMessage,
    options,
    nonEmptyFields,
    defaultOption,
    defaultOptionMessage,
    fieldRef,
    isHidden,
    hint,
  } = props;

  const {
    formState: { errors },
    control,
    watch,
  } = useFormContext();

  const { field } = useController({
    name,
    control,
    defaultValue: defaultOption?.value ? [defaultOption.value] : [],
  });
  const nonEmptyFieldsWatch = nonEmptyFields
    ? nonEmptyFields.map(nef => watch(nef))
    : null;
  const defaultOptionString = defaultOption?.value || options?.[0]?.value || '';
  const isDefaultOptionDisabled = !!(
    nonEmptyFieldsWatch?.length &&
    nonEmptyFieldsWatch.some(nefw => !isEmpty(nefw))
  );

  const selectedValues: (string | undefined)[] = Array.isArray(field.value)
    ? field.value
    : [];

  const toggle =
    (val?: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextVal = event.target.checked
        ? [...selectedValues, val]
        : selectedValues.filter(selectedValue => selectedValue !== val);

      field.onChange(nextVal);
    };

  const isOptionDisabled = (option: OptionProps) =>
    isDisabled ||
    option.isDisabled ||
    (option.value === defaultOptionString && isDefaultOptionDisabled);

  useEffect(() => {
    if (
      isDefaultOptionDisabled &&
      !selectedValues.find(v => v === defaultOptionString)
    ) {
      field.onChange([...selectedValues, defaultOptionString]);
    }
  }, [isDefaultOptionDisabled]);

  const projectType = getProjectType();
  const styles =
    projectType === ProjectTypes.PlayerPortals ? stylesPP : stylesSSO;

  if (isHidden) {
    return null;
  }

  return (
    <div
      ref={fieldRef}
      id={name}
      className={`${styles['form-element']}  ${
        errors?.[name] ? styles['form-element__has-error'] : ''
      }`}
      onFocus={() => scrollToCenter(fieldRef)}
    >
      {hasValue(label) && (
        <label className={styles['form-element__label']} htmlFor={name}>
          {label}
        </label>
      )}
      <div
        className={`${
          (options?.length ? options?.length > 1 : false)
            ? styles['form-element__checkbox-multiple']
            : styles['form-element__checkbox-single']
        }`}
      >
        {options?.map((option, index) => (
          <div
            key={`${name}_${index}`}
            className={`${styles['form-element__checkbox']} ${
              isDisabled || option.isDisabled
                ? styles['form-element__checkbox--disabled']
                : ''
            }`}
          >
            <label>
              <RichTextParser richText={option.label} />
              <input
                id={`${name}_${index}`}
                type="checkbox"
                name={field.name}
                ref={field.ref}
                onBlur={field.onBlur}
                value={option.value}
                checked={selectedValues.includes(option.value)}
                onChange={toggle(option.value)}
                disabled={isOptionDisabled(option)}
              />
              <span className={styles['form-element__checkmark']}>
                {option.value === defaultOptionString &&
                  isDefaultOptionDisabled && <FontAwesomeIcon icon={faLock} />}
              </span>
            </label>
            {option.fontAwesomeIcon && (
              <FontAwesomeIcon
                icon={byPrefixAndName.far[option.fontAwesomeIcon]}
                className={styles['form-element__icon']}
              />
            )}
          </div>
        ))}
      </div>
      {errors?.[name] ? (
        <small className={styles['form-element__hint']}>
          {validationMessage || defaultValidationMessage}
        </small>
      ) : isDefaultOptionDisabled && defaultOptionMessage ? (
        <small className={styles['form-element__hint']}>
          {defaultOptionMessage}
        </small>
      ) : hint ? (
        <small className={styles['form-element__hint']}>{hint}</small>
      ) : null}
    </div>
  );
}

export default FormCheckboxesGroup;
